import type { Epic } from 'redux-observable';
import { ofType } from 'redux-observable';
import { mergeMap, catchError } from 'rxjs/operators';
import { of, concat, from } from 'rxjs';

import { NotificationService } from 'ui-components/services/notification-service';

import { entitiesLoad } from '../entities/entities.actions';
import Global from '../global-selectors';

import { openPrintModal } from '../printing/print.actions';

import * as Actions from './ltl.actions';
import * as GridActions from './grid/ltl.grid.actions';
import * as LtlApi from '@shipstation/client-common/api/ltl.api';
import type { FreightShipmentGridRequest } from '@shipstation/client-common/types/freight-requests';
import type { FreightProvider } from '../freight-settings/freight-settings.types';

type GetFreightGridRequestAction = ReturnType<typeof GridActions.getFreightGridRequest>;
type CancelShipmentAction = ReturnType<typeof Actions.cancelShipmentRequest>;
type TrackShipmentAction = ReturnType<typeof Actions.trackShipmentRequest>;
type DownloadDocumentAction = ReturnType<typeof Actions.downloadDocumentRequest>;

const getConnectedSellerFreightProviderId = (state: unknown): number | undefined => {
  const providers: FreightProvider[] = Global.selectFreightSettingsProviders(state, {});
  const connected = providers.find((p) => p.isConnected);
  return connected?.sellerFreightProviderId ?? undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getShipmentsEpic: Epic = (action$, { getState }: any) => {
  return action$.pipe(
    ofType(GridActions.GET_FREIGHT_GRID_REQUEST),
    mergeMap((action: GetFreightGridRequestAction) => {
      const state = getState();
      const page = action.page ?? Global.selectLtlGridPage(state);
      const pageSize = action.pageSize ?? Global.selectLtlGridPageSize(state);

      const statusFilter = action.statusFilter !== undefined ? action.statusFilter : Global.selectLtlGridStatusFilter(state);
      const activeFilters = Global.selectLtlGridActiveFilters(state);

      // Build filter object from activeFilters (set via filter bar) or from URL-based statusFilter
      const mergedFilter: Record<string, unknown> = {};

      // Start with activeFilters from the filter bar
      if (activeFilters.filter) {
        Object.assign(mergedFilter, activeFilters.filter);
      }

      // If there's a URL-based statusFilter and no collection filters from the filter bar, apply it
      if (statusFilter && !mergedFilter.collectionFilters) {
        mergedFilter.collectionFilters = [
          {
            column: 'Status',
            values: [statusFilter],
            isIn: true,
          },
        ];
      }

      // When viewing "All" (no status filter), exclude Cancelled shipments by default
      if (!statusFilter && !mergedFilter.collectionFilters) {
        mergedFilter.collectionFilters = [
          {
            column: 'Status',
            values: ['Cancelled'],
            isIn: false,
          },
        ];
      }

      const hasFilter = Object.keys(mergedFilter).length > 0;

      const payload: FreightShipmentGridRequest = {
        searchTerm: (activeFilters.searchTerm as string) || '',
        orderBy: 'CreateDateUtc' as unknown as FreightShipmentGridRequest['orderBy'],
        orderByDirection: 'Descending' as unknown as FreightShipmentGridRequest['orderByDirection'],
        page: {
          pageNumber: page,
          pageSize,
        },
        ...(hasFilter ? { filter: mergedFilter as FreightShipmentGridRequest['filter'] } : {}),
      };

      return from(LtlApi.getShipmentGrid(payload)).pipe(
        mergeMap((data) =>
          concat(
            of(entitiesLoad({ entities: { freightShipments: data.freightShipments ?? {} } })),
            of(
              GridActions.getFreightGridSuccess(
                data.currentPageShipmentIds ?? [],
                data.page ?? page,
                data.pageSize ?? pageSize,
                data.totalCount ?? 0,
              ),
            ),
          ),
        ),
        catchError((err) => {
          const errorMessage = err?.response?.data?.message ?? 'Failed to load shipments.';
          return of(GridActions.getFreightGridError(errorMessage));
        }),
      );
    }),
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trackShipmentEpic: Epic = (action$, { getState }: any) => {
  return action$.pipe(
    ofType(Actions.LTL_TRACK_SHIPMENT_REQUEST),
    mergeMap((action: TrackShipmentAction) => {
      const { trackingNumber, trackingType, scac } = action;
      const sellerFreightProviderId = getConnectedSellerFreightProviderId(getState());

      if (!sellerFreightProviderId || !trackingNumber) {
        return of(Actions.trackShipmentError('Missing provider or tracking information. Please try again.'));
      }

      return from(LtlApi.trackShipment({ sellerFreightProviderId, trackingNumbers: [trackingNumber], trackingType, scac })).pipe(
        mergeMap((data) => of(Actions.trackShipmentSuccess(data))),
        catchError((err) => {
          const errorMessage = err?.response?.data?.message ?? 'Failed to track shipment.';
          return of(Actions.trackShipmentError(errorMessage));
        }),
      );
    }),
  );
};

export const cancelShipmentEpic: Epic = (action$) => {
  return action$.pipe(
    ofType(Actions.LTL_CANCEL_SHIPMENT_REQUEST),
    mergeMap((action: CancelShipmentAction) => {
      const { freightShipmentIds } = action;

      return from(LtlApi.cancelShipment({ freightShipmentIds })).pipe(
        mergeMap((data) => {
          if (!data.allSucceeded) {
            const failedResult = data.results.find((r) => !r.success);
            const errorMessage = failedResult?.errorMessage || 'Failed to cancel shipment.';
            NotificationService.showGenericFailureNotification(undefined, errorMessage);
            return of(Actions.cancelShipmentError(errorMessage));
          }
          NotificationService.showGenericSuccessNotification();
          return concat(of(Actions.cancelShipmentSuccess()), of(GridActions.getFreightGridRequest()));
        }),
        catchError((err) => {
          const errorMessage = err?.response?.data?.message ?? 'Failed to cancel shipment.';
          NotificationService.showGenericFailureNotification(undefined, errorMessage);
          return of(Actions.cancelShipmentError(errorMessage));
        }),
      );
    }),
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const downloadDocumentEpic: Epic = (action$, { getState }: any) => {
  return action$.pipe(
    ofType(Actions.LTL_DOWNLOAD_DOCUMENT_REQUEST),
    mergeMap((action: DownloadDocumentAction) => {
      const { productTransactionId, documentType } = action;
      const sellerFreightProviderId = getConnectedSellerFreightProviderId(getState());

      if (!sellerFreightProviderId || !productTransactionId) {
        return of(Actions.downloadDocumentError('Missing provider or shipment information. Please try again.'));
      }

      const proxyUrl = `/api/freight/document-download/${sellerFreightProviderId}/${productTransactionId}/${documentType}`;
      return concat(of(Actions.downloadDocumentSuccess({})), of(openPrintModal(proxyUrl, 'freight-document')));
    }),
  );
};
